import { useContext, useState } from 'react'
import {
    StyleButtonWrapper,
    StyledActionGroup,
    StyledCheckboxGroup,
    StyledDeleteAccount,
} from './DeleteAccount.styled'
import { MyAccountContext } from '../hooks/useMyAccountContext'
import React from 'react'
import { UserSettingsCheckbox } from '../components/UserSettingsCheckbox'
import { metrics } from '../../__styling'
import { UserSettingsLink } from '../components/UserSettingsLink'
import { UserSettingsButton } from '../components/UserSettingsButton'
import { tokens } from '@news-mono/design-tokens'
import {
    DeleteAccountClickProps,
    onDeleteAccountClick,
} from './helpers/delete-account-click-helper'
import { useDeleteAccount } from '../hooks/useDeleteAccount'
import { useSelector } from 'react-redux'
import { AppState, ConfigurationContext, Product } from '@news-mono/web-common'
import { useProduct } from '../../__product'

const { utility } = tokens.thenightly.colors.palette
const { pinkThulite } = tokens.perthnow.colors.palette

export const DeleteAccountAcknowledge = () => {
    const [confirmDeletion, setConfirmDeletion] = useState<boolean>(false)
    const [submitting, setSubmitting] = useState<boolean>(false)

    const product = useProduct()
    const isPn = product === Product.PerthNow

    // contexts for the account deletion
    const myAccountContext = useContext(MyAccountContext)
    const { setScreen, setScreenSelected } = myAccountContext
    const config = useContext(ConfigurationContext)

    const { userEmail, wanUserId, auth0UserId, loginProvider } = useSelector(
        (state: AppState) => state.authentication,
    )

    // back-end handler for the delete account functionality
    const { mutateAsync: deleteAccount } = useDeleteAccount({
        userId: auth0UserId || wanUserId,
        email: userEmail!,
        currentPassword: config.auth0!.passEncryptionKey!,
        passEncryptionKey: config.auth0!.passEncryptionKey!,
    })

    // props to pass through to the client-side deleteAccount helper
    const deleteAccountProps: DeleteAccountClickProps = {
        myAccountContext,
        configContext: config,
        mutateProps: {
            onDeleteAccount: deleteAccount,
            sendForgotPasswordEmail: () => {},
        },
        passwordState: {
            passwordText: config.auth0?.passEncryptionKey,
        },
    }

    return (
        <StyledDeleteAccount>
            <p>Before proceeding, please acknowledge the following:</p>
            <StyledCheckboxGroup>
                <UserSettingsCheckbox
                    name="understand-erase-data"
                    label="I wish to delete my account."
                    checked={confirmDeletion}
                    width={'fit-content'}
                    size={metrics.thenightly.margins.m2l}
                    onChange={() => setConfirmDeletion(!confirmDeletion)}
                />
            </StyledCheckboxGroup>
            <StyledActionGroup>
                <UserSettingsLink
                    color={isPn ? pinkThulite : utility.error.default}
                    onClick={async () => {
                        // Make sure the confirmation box is ticked
                        if (confirmDeletion) {
                            // If the login provider is Auth0, continue to the password screen
                            if (loginProvider === 'Auth0') {
                                setScreen('delete-account-password')
                            } else {
                                // While it's trying to submit, set the state accordingly
                                setSubmitting(true)
                                await onDeleteAccountClick(deleteAccountProps)
                                setSubmitting(false)
                            }
                        }
                    }}
                    disabled={!confirmDeletion || submitting}
                    fontWeight={600}
                    underline={false}
                >
                    Delete my account
                </UserSettingsLink>
                <StyleButtonWrapper>
                    <UserSettingsButton
                        onClick={() => {
                            setScreen('manage-account')
                            setScreenSelected(true)
                        }}
                        btnType="primary"
                        disabled={submitting}
                    >
                        Cancel
                    </UserSettingsButton>
                </StyleButtonWrapper>
            </StyledActionGroup>
        </StyledDeleteAccount>
    )
}
